import { FC, useId } from 'react'
import { Controller, SubmitHandler, useForm } from 'react-hook-form'

import { zodResolver } from '@hookform/resolvers/zod'

import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Grid,
  Label,
  Stack,
  TextArea,
  TextField,
} from '@forto-dev/phoenix-ui-core/common'

import { useLocalisation } from '@/utils/localisation'

import { SubscriptionFormSchema, SubscriptionFormSchemaType } from './schema'
import { useSubscriptionCreate } from '../api/useSubscriptionCreate'
import { SubscriptionType, subscriptionTypes } from '../domain/subscription'

export type CreateSubscriptionDialogProps = {
  onClose?: () => void
}

export const CreateSubscriptionDialog: FC<CreateSubscriptionDialogProps> = ({ onClose }) => {
  const { translate } = useLocalisation()
  const subscriptionMutation = useSubscriptionCreate()
  const id = useId()
  const {
    register,
    getValues,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(SubscriptionFormSchema),
    defaultValues: {
      eventTypes: [] as SubscriptionType[],
      url: '',
      name: '',
      description: '',
    },
  })
  const onSubmit: SubmitHandler<SubscriptionFormSchemaType> = async (data) => {
    await subscriptionMutation.mutateAsync({
      data,
    })
    onClose?.()
  }

  return (
    <Dialog
      defaultOpen
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onClose?.()
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{translate('integrations.subscriptions.create.title')}</DialogTitle>
        </DialogHeader>
        <Stack gap={16}>
          <Grid col={2} gap={16}>
            <TextField
              label={translate('integrations.subscriptions.create.fields.name.label')}
              {...register('name')}
              state={errors.name ? 'negative' : undefined}
            />

            <TextField
              type="url"
              label={translate('integrations.subscriptions.create.fields.url.label')}
              {...register('url')}
              state={errors.name ? 'negative' : undefined}
            />
          </Grid>

          <TextArea
            label={translate('integrations.subscriptions.create.fields.description.label')}
            {...register('description')}
            state={errors.description ? 'negative' : undefined}
          />

          <Stack gap={2}>
            <Label>{translate('integrations.subscriptions.create.fields.types.label')}</Label>
            <Stack gap={4}>
              {subscriptionTypes.map((type) => (
                <Controller
                  key={type}
                  render={({ field: { onChange, ...rest } }) => {
                    return (
                      <Checkbox
                        id={`${id}_${type}`}
                        label={translate(`integrations.subscriptions.types.${type}`)}
                        checked={getValues('eventTypes').includes(type)}
                        onCheckedChange={(checked) => {
                          onChange(
                            checked
                              ? [...getValues('eventTypes'), type]
                              : getValues('eventTypes').filter((item) => item !== type)
                          )
                        }}
                        {...rest}
                      />
                    )
                  }}
                  control={control}
                  name="eventTypes"
                />
              ))}
            </Stack>
          </Stack>
        </Stack>
        <DialogFooter>
          <Button onClick={handleSubmit(onSubmit)}>
            {translate('integrations.subscriptions.create.actions.create')}
          </Button>
          <Button
            variant="outline-primary"
            onClick={() => {
              onClose?.()
            }}
          >
            {translate('integrations.subscriptions.create.actions.cancel')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
